// pages/PlatformUserManagement/components/AuditLogTable.jsx
// Immutable audit log viewer.
// Matches your Bootstrap table style (same as MemberManagementPage table).

import React, { useState, useEffect } from "react";
import { fetchAuditLogs } from "./PlatformUserAction";

const ACTION_BADGE = {
  LOGIN:           "badge text-outline-success",
  LOGOUT:          "badge text-outline-secondary",
  LOGIN_FAILED:    "badge text-outline-danger",
  SESSION_TIMEOUT: "badge text-outline-warning",
  PU_CREATED:      "badge text-outline-primary",
  PU_UPDATED:      "badge text-outline-info",
  PU_DEACTIVATED:  "badge text-outline-danger",
  PERM_CHANGED:    "badge text-outline-warning",
  CREDIT:          "badge text-outline-success",
  DEBIT:           "badge text-outline-danger",
  TXN_REVERSAL:    "badge text-outline-warning",
  MEMBER_VIEWED:   "badge text-outline-secondary",
  MEMBER_EDITED:   "badge text-outline-info",
  REPORT_VIEWED:   "badge text-outline-secondary",
  REPORT_EXPORTED: "badge text-outline-info",
};

export default function AuditLogTable({ refresh = 0 }) {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [search,  setSearch]  = useState("");

  useEffect(() => {
    setLoading(true);
    fetchAuditLogs()
      .then((res) => setLogs(res?.data || []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [refresh]);

  const filtered = logs.filter((l) =>
    !search ||
    l.action_label?.toLowerCase().includes(search.toLowerCase()) ||
    l.target?.toLowerCase().includes(search.toLowerCase()) ||
    l.target_phone?.includes(search) ||
    l.description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="card mb-0">
      <div className="card-header d-flex align-items-center justify-content-between py-2 flex-wrap gap-2">
        <span className="f-w-600" style={{ fontSize: 14 }}>
          <i className="ph-duotone ph-shield-check me-2 text-primary" />
          Audit Log
          <span className="badge bg-secondary ms-2" style={{ fontSize: 10 }}>
            {logs.length} records
          </span>
        </span>

        {/* Search */}
        <div className="input-group input-group-sm" style={{ maxWidth: 260 }}>
          <span className="input-group-text">
            <i className="ph-duotone ph-magnifying-glass" />
          </span>
          <input
            className="form-control"
            placeholder="Search logs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="btn btn-outline-secondary" onClick={() => setSearch("")}>
              <i className="ph-duotone ph-x" />
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-4">
          <span className="spinner-border spinner-border-sm text-primary me-2" />
          Loading audit log...
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover table-sm mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ fontSize: 12, whiteSpace: "nowrap" }}>Timestamp</th>
                <th style={{ fontSize: 12 }}>Action</th>
                <th style={{ fontSize: 12 }}>Target</th>
                <th style={{ fontSize: 12 }}>Phone</th>
                <th style={{ fontSize: 12 }}>Amount</th>
                <th style={{ fontSize: 12 }}>Description</th>
                <th style={{ fontSize: 12 }}>IP Address</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4" style={{ fontSize: 13 }}>
                    <i className="ph-duotone ph-list-magnifying-glass me-2" />
                    {search ? "No matching records." : "No audit records found."}
                  </td>
                </tr>
              )}
              {filtered.map((log) => (
                <tr key={log.id}>
                  <td style={{ fontSize: 11, whiteSpace: "nowrap", color: "#6c757d" }}>
                    {log.timestamp}
                  </td>
                  <td>
                    <span className={ACTION_BADGE[log.action] || "badge text-outline-secondary"} style={{ fontSize: 10 }}>
                      {log.action_label}
                    </span>
                  </td>
                  <td style={{ fontSize: 12 }}>{log.target || "—"}</td>
                  <td style={{ fontSize: 12 }}>{log.target_phone || "—"}</td>
                  <td style={{ fontSize: 12 }}>
                    {log.amount ? (
                      <span className={log.action === "CREDIT" ? "text-success f-w-600" : "text-danger f-w-600"}>
                        ₹{Number(log.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    ) : "—"}
                  </td>
                  <td style={{ fontSize: 12, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {log.description || "—"}
                  </td>
                  <td style={{ fontSize: 11, color: "#6c757d" }}>{log.ip_address}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}